import type { Cart, Money, Product, ProductVariant } from '@/lib/shopify/types';

/**
 * Black Friday + free shipping. The discount itself is an AUTOMATIC discount created in Shopify (no
 * code); this file only decides what the site ANNOUNCES. Shopify stays the source of truth for what is
 * charged: the cart always shows Shopify's own numbers (see CartDrawer / discountAllocations).
 *
 * Off by default. Turn it on with the build variable NEXT_PUBLIC_BLACK_FRIDAY=on, only after
 * `node scripts/check-promo.mjs` confirms that Shopify really charges the announced price and ships
 * for free. (NEXT_PUBLIC_PROMO_SALE_PRICE only exists to test with another price locally.)
 */
export const PROMO = {
  enabled: process.env.NEXT_PUBLIC_BLACK_FRIDAY === 'on',
  /** Regular price of the variant; the announcement is hidden if Shopify's price is any other. */
  regularPrice: '64.99',
  /** Effective price of ONE unit with the automatic discount applied. */
  salePrice: process.env.NEXT_PUBLIC_PROMO_SALE_PRICE?.trim() || '54.90',
  /** Last instant of the promotion: 30/11/2026 23:59:59 Europe/Madrid (CET, UTC+1) = 22:59:59 UTC. */
  endsAt: Date.UTC(2026, 10, 30, 22, 59, 59),
  /**
   * Countries where Shopify's shipping rate is really 0 €. The store only sells to Spain today
   * (verified with the Storefront API); add a country here only after checking its rate.
   */
  freeShippingCountries: ['ES'],
} as const;

export const promoWindowOpen = (now: number = Date.now()) => PROMO.enabled && now <= PROMO.endsAt;

const sameAmount = (a: string | number, b: string | number) => Math.abs(Number(a) - Number(b)) < 0.005;

/** The sale price may only be announced for a variant whose Shopify price is the regular one. */
export function salePriceFor(variant: ProductVariant | null): Money | null {
  if (!variant) return null;
  if (variant.compareAtPrice) return null; // already discounted in Shopify: do not stack announcements
  if (variant.price.currencyCode !== 'EUR' || !sameAmount(variant.price.amount, PROMO.regularPrice)) return null;
  return { amount: PROMO.salePrice, currencyCode: variant.price.currencyCode };
}

/**
 * True when Shopify's cart contradicts the announced price: a line of this product with ONE unit
 * whose total is not the sale price. (More units are not checked: how the discount scales is up to
 * Shopify.) The site then stops announcing the sale price instead of showing a figure that the
 * checkout will not honour.
 */
export function cartContradictsPromo(cart: Cart, product: Product | null): boolean {
  if (!product) return false;
  return cart.lines.some(
    (line) =>
      line.merchandise.product.handle === product.handle &&
      line.quantity === 1 &&
      !sameAmount(line.cost.totalAmount.amount, PROMO.salePrice)
  );
}
